import { useState, useRef, useCallback, useEffect } from "react";
import { PositionType } from "./types";

interface UseDraggableProps {
  onDrag?: (position: PositionType) => void;
  disabled?: boolean;
  initialPosition?: PositionType;
}

export const useDraggable = ({
  onDrag,
  disabled = false,
  initialPosition,
}: UseDraggableProps) => {
  const getCenterPosition = () => ({
    x: (window.innerWidth - 400) / 2,
    y: (window.innerHeight - 300) / 2,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState(
    initialPosition || getCenterPosition()
  );
  const dragRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef({ x: 0, y: 0 });

  const handleMouseDownForDrag = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;

      e.preventDefault();
      setIsDragging(true);

      const rect = dragRef.current?.getBoundingClientRect();
      if (rect) {
        offsetRef.current = {
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        };
      }
    },
    [disabled]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const newPosition = {
        x: e.clientX - offsetRef.current.x,
        y: e.clientY - offsetRef.current.y,
      };

      setPosition(newPosition);
      onDrag?.(newPosition);
    },
    [isDragging, onDrag]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return {
    dragRef,
    isDragging,
    position,
    setPosition,
    handleMouseDownForDrag,
  };
};
